import { useEffect, useRef, useState } from "react";
import type { Sort } from "../api/generated";
import { SORT_OPTIONS, groupingWords, sortWords, type Grouping } from "../domain/views";

interface ViewDefinitionProps {
  sort: Sort;
  grouping: Grouping;
  /** Offers "Save as filter" when given. */
  onSaveAsFilter?(): void;
  onChangeSort(sort: Sort): void;
}

/** Says in plain words how the list is ordered and grouped, and lets the user change the order. */
export function ViewDefinition({ sort, grouping, onSaveAsFilter, onChangeSort }: ViewDefinitionProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const groupedBy = groupingWords(grouping);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const choose = (option: Sort) => {
    setOpen(false);
    trigger.current?.focus();
    onChangeSort(option);
  };

  return (
    <p className="view-definition">
      <span className="sort-menu" ref={root}>
        Sorted by{" "}
        <button
          ref={trigger}
          type="button"
          className="link-button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Sort order: ${sortWords(sort)}. Change`}
          onClick={() => setOpen((current) => !current)}
        >
          {sortWords(sort)} ▾
        </button>
        {open && (
          <div
            className="menu"
            role="menu"
            aria-label="Sort by"
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const items = [...event.currentTarget.querySelectorAll<HTMLElement>("[role=menuitemradio]")];
                const current = items.indexOf(document.activeElement as HTMLElement);
                const step = event.key === "ArrowDown" ? 1 : -1;
                items[(current + step + items.length) % items.length]?.focus();
              } else if (event.key === "Escape") {
                event.stopPropagation();
                setOpen(false);
                trigger.current?.focus();
              }
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={option === sort}
                className="menu-item"
                autoFocus={option === sort}
                onClick={() => choose(option)}
              >
                <span aria-hidden="true">{option === sort ? "✓" : ""}</span>
                {sortWords(option)}
              </button>
            ))}
          </div>
        )}
      </span>
      {groupedBy !== undefined && <span>, grouped by {groupedBy}</span>}
      {onSaveAsFilter !== undefined && (
        <button type="button" className="link-button save-filter" onClick={onSaveAsFilter}>
          Save as filter
        </button>
      )}
    </p>
  );
}
